import { type ReactNode, useEffect, useMemo, useRef } from 'react'
import { InputGroup } from '@systemeio/components'
import { Send03Icon } from '@systemeio/icons'
import { EditorContent, EditorContext, useEditor, useEditorState } from '@tiptap/react'
import { cn, twJoin } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import AttachmentsPreview from 'modules/attachments/components/preview'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { EditorMode } from 'modules/comments/types'
import { docToHtml, htmlToDoc } from 'modules/comments/utils/comment-html'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { useMentionSuggestion } from 'modules/community/components/editor/extensions/mention-suggestion'
import {
  AttachmentUploadStatusEnum,
  type PostAttachmentType,
} from 'modules/community/types/post-attachment'
import { useCommentsContext } from '../comments-list'
import { createCommentExtensions } from './extensions'
import { TiptapToolbar } from './tiptap-toolbar'

export interface CommentEditorProps {
  handleAddComment: (value: string, attachments?: PostAttachmentType[]) => void
  isSubmittingAttachments?: boolean
  setActiveComment: (comment: CommentInterface | null) => void
  isRoot: boolean
  initialHtml?: string
  initialMentions?: MentionInterface[]
  mode?: EditorMode
  onCancel?: () => void
  className?: string
  editableComment?: CommentInterface
}

export const CommentEditor = ({
  handleAddComment,
  isSubmittingAttachments = false,
  setActiveComment,
  isRoot,
  initialHtml,
  initialMentions,
  mode = EditorMode.Add,
  onCancel,
  className = '',
  editableComment,
}: CommentEditorProps): ReactNode => {
  const { user } = useUser()
  const { t } = useTranslation()
  const { extra } = useCommentsContext()
  const { attachments, isUploading } = usePostAttachmentsContext()
  const html = editableComment?.text ?? initialHtml ?? ''
  const mentions = editableComment?.mentions ?? initialMentions ?? []
  const community = isCommunityExtra(extra) ? extra.community : undefined
  const communityRef = useRef(community)
  communityRef.current = community
  const mentionSuggestion = {
    ...useMentionSuggestion(() => communityRef.current),
    placement: 'top-start' as const,
  }

  const editor = useEditor({
    extensions: createCommentExtensions(mentionSuggestion),
    content: htmlToDoc(html, mentions),
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    autofocus: !isRoot || mode === EditorMode.Edit ? 'end' : false,
    editorProps: {
      attributes: {
        'data-testid': 'comment-editor-input',
        dir: 'auto',
        class:
          'comment-input max-h-[max(30svh,5rem)] w-full overflow-y-auto pr-2 wrap-break-word whitespace-break-spaces text-start text-text-primary focus-visible:outline-hidden [&_a]:text-brand-primary [&_a]:no-underline [&_a:hover]:underline',
      },
    },
  })

  const previousHtmlRef = useRef(html)
  const mentionsRef = useRef(mentions)
  mentionsRef.current = mentions

  useEffect(() => {
    if (!editor || previousHtmlRef.current === html) return
    previousHtmlRef.current = html
    editor.commands.setContent(htmlToDoc(html, mentionsRef.current), { emitUpdate: false })
    editor.commands.focus('end')
  }, [editor, html])

  const hasContent =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) => {
        if (!currentEditor || currentEditor.isEmpty) return false

        return !!currentEditor.getText().trim()
      },
    }) ?? false
  const editorContext = useMemo(() => ({ editor }), [editor])
  const canSubmit =
    hasContent &&
    !isUploading &&
    !isSubmittingAttachments &&
    !attachments?.some(({ status }) => status === AttachmentUploadStatusEnum.Failed)

  const cancel = (): void => {
    onCancel?.()
    setActiveComment(null)
  }

  const handleSendComment = (): void => {
    if (!editor || !canSubmit) return
    handleAddComment(docToHtml(editor.getJSON()), attachments)
    if (mode !== EditorMode.Edit) editor.commands.clearContent(true)
  }

  if (!editor) return null

  return (
    <div className={className} data-testid="comment-editor">
      <div className="flex flex-row items-start gap-2.5 sm:gap-2.5">
        {isAuthorizedUser(user) && (
          <img alt="avatar" className="mt-2 size-10 rounded-full" src={user.avatarUrl} />
        )}

        <InputGroup.Root
          onClick={event => {
            if (event.target === event.currentTarget) editor.commands.focus('end')
          }}
          data-expanded={hasContent || mode === EditorMode.Edit || undefined}
          className={cn(
            'group/comment-editor relative h-auto w-[calc(100%-40px)] min-w-0 grow-0 cursor-text flex-col items-stretch border border-transparent p-3 transition-all duration-200',
            'focus-within:p-5 focus-within:pr-14 focus-within:pb-12 has-data-popup-open:p-5 has-data-popup-open:pr-14 has-data-popup-open:pb-12 data-expanded:p-5 data-expanded:pr-14 data-expanded:pb-12',
          )}
          onKeyDown={event => {
            if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target))
              return
            if (event.key === 'Escape' && (!isRoot || mode === EditorMode.Edit)) cancel()
          }}
          onKeyDownCapture={event => {
            if (
              event.key !== 'Enter' ||
              !event.ctrlKey ||
              event.nativeEvent.isComposing ||
              !(event.target instanceof Node) ||
              !event.currentTarget.contains(event.target)
            )
              return
            event.preventDefault()
            if (!event.repeat) handleSendComment()
          }}
        >
          {!hasContent && (
            <span
              className={twJoin(
                'pointer-events-none absolute top-3 left-3 text-text-secondary/50',
                'group-focus-within/comment-editor:top-5 group-focus-within/comment-editor:left-5 group-has-data-popup-open/comment-editor:top-5 group-has-data-popup-open/comment-editor:left-5 group-data-expanded/comment-editor:top-5 group-data-expanded/comment-editor:left-5',
              )}
            >
              {t('comments.components.comments.input_placeholder_label')}
            </span>
          )}
          <EditorContent editor={editor} data-group-control />
          <AttachmentsPreview className={twJoin(!!attachments?.length && `pt-4`)} />

          <EditorContext.Provider value={editorContext}>
            <TiptapToolbar
              path={community?.communityPath}
              className="absolute bottom-2.5 left-3.5 hidden group-focus-within/comment-editor:flex group-has-data-popup-open/comment-editor:flex group-data-expanded/comment-editor:flex"
            />
          </EditorContext.Provider>
          <div className="hidden group-focus-within/comment-editor:contents group-has-data-popup-open/comment-editor:contents group-data-expanded/comment-editor:contents">
            {mode !== EditorMode.Edit && !isRoot && (
              <button
                className="text-brand-primary hover:text-brand-primary-hover absolute right-3.5 bottom-2.5 block cursor-pointer text-sm md:hidden"
                onClick={cancel}
              >
                {t('comments.components.comments.cancel_label')}
              </button>
            )}
            <button
              data-testid="comment-editor-submit"
              className="text-brand-primary absolute top-5 right-6 flex size-6 items-center justify-center disabled:text-text-secondary"
              onClick={handleSendComment}
              disabled={!canSubmit}
            >
              <Send03Icon className="size-5 md:size-6" />
            </button>
          </div>
        </InputGroup.Root>
      </div>
      {(!isRoot || mode === EditorMode.Edit) && (
        <button
          data-testid="comment-editor-cancel"
          onClick={cancel}
          className="ml-12 hidden cursor-pointer pt-2.5 text-sm text-text-primary/50 transition-colors hover:text-text-primary md:inline-block"
          dangerouslySetInnerHTML={{
            __html: t('comments.components.comments.cancel_label_or_esc'),
          }}
        />
      )}
      {mode === EditorMode.Edit && (
        <div className="mt-2.5 flex items-center justify-end">
          <button
            className="bg-brand-primary text-background-secondary z-1 flex min-h-9 w-auto min-w-20 cursor-pointer items-center justify-end self-center rounded-sm px-5 text-center text-sm font-bold outline-0 select-none md:hidden"
            onClick={cancel}
          >
            {t('comments.components.comments.cancel_label')}
          </button>
        </div>
      )}
    </div>
  )
}
